import { FC, useState, useEffect, useCallback } from "react"
import { Paper } from "@mui/material"
import {
  DataGridPremium,
  useGridApiRef,
  GridPaginationModel,
  gridFilteredTopLevelRowCountSelector,
  gridFilterModelSelector,
  gridClasses,
  GridColDef,
  GridPinnedColumns,
} from '@mui/x-data-grid-premium'
import { TableToolbar } from "src/components/ui"
import { Report } from "src/api"
import { SearchParamsKeys } from "src/enums"
import { defaultRowsValuePerPage } from 'src/config'
import { ReportColumnsDataGrid } from "./tableColumns"

const defaultColumns = [
  ReportColumnsDataGrid.Name,
  ReportColumnsDataGrid.Status,
  ReportColumnsDataGrid.Type,
  ReportColumnsDataGrid.ReportProject,
  ReportColumnsDataGrid.ReportSection,
  ReportColumnsDataGrid.DateFrom,
  ReportColumnsDataGrid.DateTo,
  ReportColumnsDataGrid.Duration,
  ReportColumnsDataGrid.DateOfGeneration,
  ReportColumnsDataGrid.TimeOfGeneration,
  ReportColumnsDataGrid.DayOfWeek,
  ReportColumnsDataGrid.Contractor,
  ReportColumnsDataGrid.Author,
  ReportColumnsDataGrid.ProjectArchivationDate,
  ReportColumnsDataGrid.AvailabilitySummary,
  ReportColumnsDataGrid.NameHadChanged,
  ReportColumnsDataGrid.EventsIncluded,
  ReportColumnsDataGrid.EventsAmount,
  ReportColumnsDataGrid.EventsWithPhoto,
  ReportColumnsDataGrid.EventsWithNotes,
]

interface ReportListProps {
  count?: number
  data?: Report[]
  isLoading?: boolean
  autoHeight?: boolean
  queryParams?: Record<string, string>
  setQueryParams?: (params: Record<string, string>, paramsToRemove?: string[]) => void
  defaultRowsPerPageOptions?: number[]
  columns?: GridColDef<Report>[]
}

export const ReportList: FC<ReportListProps> = ({
  data = [],
  count = 0,
  isLoading = false,
  autoHeight = false,
  columns = defaultColumns,
  setQueryParams,
  queryParams,
  defaultRowsPerPageOptions
}) => {
  const apiRef = useGridApiRef()
  const [rowCountState, setRowCountState] = useState(count)
  const [pinnedColumns, setPinnedColumns] = useState<GridPinnedColumns>({
    right: ["actions"]
  })

  useEffect(() => {
    setRowCountState((prevRowCountState) =>
      count !== undefined ? count : prevRowCountState
    )
  }, [count, setRowCountState])

  const handleChangePaginationModel = async (pagination: GridPaginationModel): Promise<void> => {
    if (!setQueryParams) return

    const { page, pageSize } = pagination
    const newPage = page + 1
    setQueryParams({
      [SearchParamsKeys.Page]: `${newPage}`,
      [SearchParamsKeys.RowsPerPage]: `${pageSize}`,
    }, [
      SearchParamsKeys.Page,
      SearchParamsKeys.RowsPerPage,
    ])
  }

  const handleChangeState = async () => {
    const filterModel = gridFilterModelSelector(apiRef.current.state)
    if (filterModel.items.length > 0 && filterModel.items[0].value) {
      const visibleRowCount = gridFilteredTopLevelRowCountSelector(apiRef.current.state)
      if (visibleRowCount) setRowCountState(visibleRowCount)
    }
    else setRowCountState(count)
  }

  const handlePinnedColumnsChange = useCallback(
    (updatedPinnedColumns: GridPinnedColumns) => {
      setPinnedColumns(updatedPinnedColumns)
    },
    []
  )

  const page = queryParams?.page ? parseInt(queryParams.page, 10) - 1 : 0
  const pageSize = parseInt(queryParams?.rowsPerPage || '', 10) || defaultRowsPerPageOptions?.[0] || defaultRowsValuePerPage[0]

  return (
    <Paper sx={{ flexGrow: 1, marginTop: 3, minHeight: '200px', height: '100%' }} >
      <DataGridPremium<Report>
        apiRef={apiRef}
        pagination
        autoHeight={autoHeight}
        pinnedColumns={pinnedColumns}
        disableRowSelectionOnClick
        rows={data}
        columns={columns}
        rowCount={rowCountState}
        loading={isLoading}
        paginationModel={{ page, pageSize }}
        rowThreshold={2}
        getRowHeight={() => 'auto'}
        columnThreshold={2}
        disableVirtualization
        paginationMode="server"
        pageSizeOptions={defaultRowsPerPageOptions}
        onPaginationModelChange={handleChangePaginationModel}
        onStateChange={handleChangeState}
        localeText={{
          columnMenuSortAsc: "Sort A-Z",
          columnMenuSortDesc: "Sort Z-A",
        }}
        slots={{
          toolbar: TableToolbar
        }}
        onPinnedColumnsChange={handlePinnedColumnsChange}
        sx={(theme) => ({
          border: "none",
          "& .MuiDataGrid-columnHeaderTitleContainerContent": {
            paddingLeft: "5px",
            overflow: "visible",
          },
          "& .MuiDataGrid-columnHeaderTitle": {
            whiteSpace: "normal",
            lineHeight: 1,
            fontSize: "13px",
            fontWeight: 400,
            color: "rgba(0, 0, 0, 0.6)",
            overflow: "visible",
          },
          [`& .${gridClasses.cell}`]: {
            py: 1,
          },
        })}
      />
    </Paper >
  )
}